import type {QueryKey} from '@tanstack/react-query';
import {useMemo} from 'react';
import recruitmentsFetchers from '@/api/fetchers/recruitments';
import type {
  ApiRecruitmentsResponse,
  Recruitment,
  RecruitmentFilterStatus,
  RecruitmentsResponse,
  RecruitmentSortBy,
  SortOrder,
} from '@/views/creators/recruit-crm/types';
import useGuardedFetchNextPage from '@/hooks/use-guarded-fetch-next-page';
import useInfiniteReactQuery from '@/hooks/use-infinite-react-query';

export const recruitmentsQueryKey = 'recruitments';

const RECRUITMENTS_QUERY_STALE_MS = 30000;

/** Query key prefix for all recruitments queries for one agency (for prefix matching). */
export function getRecruitmentsQueryKeyForAgency(agencyId: string): QueryKey {
  return [recruitmentsQueryKey, agencyId];
}

export interface UseRecruitmentsQueryParams {
  agencyId: string;
  status?: RecruitmentFilterStatus[];
  sortBy?: RecruitmentSortBy;
  sortOrder?: SortOrder;
  perPage?: number;
  enabled?: boolean;
}

function selectPage(data: ApiRecruitmentsResponse): RecruitmentsResponse {
  return {
    recruitments: data.recruitments.map(
      (recruitment): Recruitment => ({
        id: recruitment.id,
        userId: recruitment.userId,
        profileId: recruitment.user?.profile?.id || null,
        email: recruitment.email,
        firstName: recruitment.user?.firstName || null,
        lastName: recruitment.user?.lastName || null,
        agencyId: recruitment.agencyId,
        status: recruitment.status,
        removed: recruitment.removed,
        createdAt: recruitment.createdAt,
        updatedAt: recruitment.updatedAt,
      })
    ),
    meta: data.meta,
  };
}

function useRecruitmentsQuery(params: UseRecruitmentsQueryParams) {
  const {
    agencyId,
    status,
    sortBy = 'created_at',
    sortOrder = 'desc',
    perPage = 25,
    enabled = true,
  } = params;

  const response = useInfiniteReactQuery<
    ApiRecruitmentsResponse,
    unknown,
    RecruitmentsResponse
  >({
    queryKey: [
      ...getRecruitmentsQueryKeyForAgency(agencyId),
      {status, sortBy, sortOrder, perPage},
    ],
    queryFn: ({pageParam = 1}) => {
      const page = typeof pageParam === 'number' ? pageParam : 1;
      return recruitmentsFetchers.list({
        agencyId,
        status,
        sortBy,
        sortOrder,
        page,
        perPage,
      });
    },
    select: (data) => ({
      pages: data.pages.map(selectPage),
      pageParams: data.pageParams,
    }),
    getNextPageParam: (lastPage: ApiRecruitmentsResponse) => {
      const {currentPage, totalPages} = lastPage.meta;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    enabled: enabled && !!agencyId,
    staleTime: RECRUITMENTS_QUERY_STALE_MS,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const recruitments = useMemo(
    () => response.data?.pages.flatMap((page) => page.recruitments) ?? [],
    [response.data]
  );

  const meta = response.data?.pages[0]?.meta;

  const fetchNextPage = useGuardedFetchNextPage(
    response.fetchNextPage,
    response.isFetching || response.isLoading
  );

  return {
    recruitments,
    meta,
    isLoading: response.isLoading,
    isFetching: response.isFetching,
    fetchNextPage,
    hasNextPage: response.hasNextPage,
  };
}

export default useRecruitmentsQuery;
